import { useState, useEffect, useRef } from "react";
import { studyProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { AttentionReportsSkeleton } from "@/features/reports/components/attention-reports-skeleton";
import { StudiesLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { useGlobalStore } from "@/stores/global-store";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { Progress } from "@/components/ui/progress";

/* Constants */
const POLL_INTERVAL = 3000;

/* Prop Types */
interface StudyAssetProcessRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudyAssetProcessRoute component
 *
 * Triggers study-level processing and polls until every asset reaches a terminal state.
 */
export const StudyAssetProcessRoute = ({ projectId, studyId }: StudyAssetProcessRouteProps) => {
  /* Stores */
  const entitlements = useGlobalStore((state) => state.entitlements);

  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const hasStarted = useRef(false);

  /* Refs */
  const videoRef = useRef<HTMLVideoElement>(null);

  /* Hooks */
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-process");

  /* Actions */
  const { setContextPackIds } = useEditorStore((state) => state.editorActions);

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Side Effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startProcessing = async () => {
      setIsActive(true);

      await studyProcess(projectId, studyId);

      // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
      while (true) {
        const statuses = await getStudyStatus(projectId, studyId, "process");
        setAssetStatuses(statuses);

        const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
        if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }

      // Reset the context pack IDs in the editor store
      setContextPackIds([]);

      navigateToNext();
    };

    void startProcessing();
  }, []);

  // Check if user has access to Memory & Emotion features
  const hasMemoryEmotion = entitlements.some((entitlement) => entitlement.name === "memoryEmotion");

  // Messages configuration based on M&E access
  const messages = hasMemoryEmotion
    ? [
        { metric: "Attention", color: "text-[#19D3C8]", description: "Analysing assets..." },
        { metric: "Emotion", color: "text-primary", description: "Running algorithm..." },
        { metric: "Memory", color: "text-[#9747FF]", description: "Finalising..." },
      ]
    : [
        { metric: "Analyzing", color: "", description: "Analyzing scores..." },
        { metric: "Building", color: "", description: "Building graphs..." },
        { metric: "Running", color: "", description: "Running algorithm..." },
      ];

  // Different animation based on M&E access
  const animationSrc = hasMemoryEmotion ? "/videos/process-animation-m&e.webm" : "/videos/process-animation.webm";

  // Sync text with video animation using video timeupdate event
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const { currentTime, duration } = video;
      if (!duration) return;

      const segmentDuration = duration / messages.length;
      const newIndex = Math.floor(currentTime / segmentDuration) % messages.length;

      setCurrentMessageIndex(newIndex);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [messages]);

  const currentMessage = messages[currentMessageIndex];

  const renderHeaderText = () => {
    if (!hasMemoryEmotion) {
      return (
        <h2>
          Processing {succeededCount} of {totalCount || "..."}
        </h2>
      );
    }

    return (
      <>
        <h2>
          Getting <span className={`${currentMessage.color} transition-colors duration-200`}>{currentMessage.metric}</span> scores
        </h2>
        <p className="text-sm text-base-content/70">
          {succeededCount} of {totalCount || "..."}
        </p>
      </>
    );
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="reports">
      {isActive && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video ref={videoRef} src={animationSrc} className={`${hasMemoryEmotion ? "w-60" : "w-48"} pb-2`} autoPlay muted loop />
            {renderHeaderText()}
            {totalCount > 0 && <Progress value={(succeededCount / totalCount) * 100} staticColorClass="bg-success" className="w-1/2" />}
            <p>{currentMessage.description}</p>
          </div>
        </FullPageLoader>
      )}
      <AttentionReportsSkeleton title={hasMemoryEmotion ? "Creative Impact" : "Attention"} />
    </StudiesLayout>
  );
};
